import { gql, useQuery } from '@apollo/client';
import { isDefined } from '@prismamedia/one-components';
import { useEffect, useMemo, useState } from 'react';
import {
  GetRecipeQuery,
  GetRecipeQueryVariables,
  GetRecipesQuery,
  GetRecipesQueryVariables,
} from '../../__generated__/queries-recipe';
import { getRecommendedRecipes } from '../../utils/recommendSearch';
import { RECIPE_FRAGMENT } from '../fragments/recipe.recipe.graphql';

export const GET_RECIPES = gql`
  query GetRecipes(
    $first: Int!
    $skip: Int
    $orderBy: [RecipeOrderByInput!]
    $where: RecipeWhereInput!
  ) {
    recipeCount(where: $where)
    recipes(first: $first, skip: $skip, orderBy: $orderBy, where: $where) {
      ...Recipe
    }
  }
  ${RECIPE_FRAGMENT}
`;

export const useGetRecipes = (
  variables: GetRecipesQueryVariables,
  skipQuery?: boolean,
) => {
  const [recommendLoading, setRecommendLoading] = useState(false);
  const [searchIds, setSearchIds] = useState<string[] | undefined>(undefined);
  const [recommendError, setRecommendError] = useState<Error | undefined>(
    undefined,
  );

  const {
    where: { title_contains, brandKey, status, AND, ...whereRest },
    ...variablesRest
  } = variables;

  const useRecommend = useMemo(() => {
    const countRestParams = Object.values(whereRest).filter(isDefined).length;
    return (
      !!title_contains &&
      status === 'Published' &&
      !AND?.length &&
      !countRestParams
    );
  }, [AND?.length, status, title_contains, whereRest]);

  useEffect(() => {
    setRecommendError(undefined);

    if (!useRecommend || !title_contains) {
      setSearchIds(undefined);
      setRecommendLoading(false);
      return;
    }

    setRecommendLoading(true);
    getRecommendedRecipes(title_contains, brandKey, 100)
      .then((recipes) =>
        setSearchIds(recipes?.map(({ idone }) => idone).filter(isDefined)),
      )
      .catch((e) => setRecommendError(e))
      .finally(() => setRecommendLoading(false));
  }, [brandKey, title_contains, useRecommend]);

  const { loading, error, ...rest } = useQuery<
    GetRecipesQuery,
    GetRecipesQueryVariables
  >(GET_RECIPES, {
    variables: {
      ...variablesRest,
      where: {
        id_in: useRecommend ? searchIds || [] : undefined,
        title_contains: useRecommend ? undefined : title_contains,
        brandKey,
        status,
        AND,
        ...whereRest,
      },
    },
    skip: skipQuery,
    notifyOnNetworkStatusChange: true,
  });

  return {
    ...rest,
    loading: loading || recommendLoading,
    error: error || recommendError,
  };
};

const GET_RECIPE = gql`
  query GetRecipe($where: RecipeWhereUniqueInput!) {
    recipe(where: $where) {
      ...Recipe
    }
  }
  ${RECIPE_FRAGMENT}
`;

export const useGetRecipe = (id: string, skip?: boolean) =>
  useQuery<GetRecipeQuery, GetRecipeQueryVariables>(GET_RECIPE, {
    variables: { where: { id } },
    skip: !id || skip,
  });
